<template>
  <div class="franchise-sync-issues">
    <div class="issues-toolbar">
      <select v-model="filterType" @change="resetAndLoad">
        <option value="">All issue types</option>
        <option value="upsert_error">Upsert errors</option>
        <option value="duplicate">Duplicates</option>
        <option value="gap_fc_not_db">In FC, not in DB</option>
        <option value="gap_db_not_fc">In DB, not in FC</option>
      </select>
      <select v-model="filterStatus" @change="resetAndLoad">
        <option value="">All statuses</option>
        <option value="open">Open</option>
        <option value="acknowledged">Acknowledged</option>
        <option value="dismissed">Dismissed</option>
        <option value="resolved">Resolved</option>
      </select>
      <button class="btn-refresh" :disabled="loading" @click="loadIssues">
        <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
        Refresh
      </button>
    </div>

    <div v-if="loading && issues.length === 0" class="loading-row">
      <i class="fas fa-spinner fa-spin"></i> Loading sync issues...
    </div>

    <div v-else-if="error" class="error-row">{{ error }}</div>

    <div v-else-if="issues.length === 0" class="empty-row">
      <i class="fas fa-check-circle"></i>
      <p>No sync issues match your filters.</p>
    </div>

    <div v-else class="issues-list">
      <div
        v-for="issue in issues"
        :key="issue.issue_key"
        class="issue-card"
        :class="`severity-${issue.severity}`"
      >
        <div class="issue-header">
          <span class="issue-type-badge">{{ issueTypeLabel(issue.issue_type) }}</span>
          <span class="issue-status" :class="`status-${issue.review_status}`">
            {{ issue.review_status || 'open' }}
          </span>
        </div>
        <h4>{{ issue.title }}</h4>
        <p v-if="issue.description" class="issue-desc">{{ issue.description }}</p>

        <div class="issue-actions">
          <router-link
            v-if="issue.subject_id"
            :to="entityLink(issue.subject_id)"
            class="btn-link"
          >
            <i class="fas fa-external-link-alt"></i> Open Entity Record
          </router-link>

          <template v-if="canReview">
            <button
              v-if="issue.review_status === 'open' || !issue.review_status"
              class="btn-action btn-ack"
              :disabled="actionLoading === issue.issue_key"
              @click="reviewIssue(issue, 'acknowledged')"
            >
              Acknowledge
            </button>
            <button
              v-if="issue.review_status === 'open' || !issue.review_status"
              class="btn-action btn-dismiss"
              :disabled="actionLoading === issue.issue_key"
              @click="reviewIssue(issue, 'dismissed')"
            >
              Dismiss
            </button>
            <button
              v-if="issue.review_status && issue.review_status !== 'resolved'"
              class="btn-action btn-resolve"
              :disabled="actionLoading === issue.issue_key"
              @click="reviewIssue(issue, 'resolved')"
            >
              Mark Resolved
            </button>
          </template>
        </div>

        <div v-if="issue.reviewed_by" class="review-audit">
          {{ issue.review_status }} by {{ issue.reviewed_by }}
          <span v-if="issue.reviewed_at"> · {{ formatDateTime(issue.reviewed_at) }}</span>
        </div>
      </div>

      <div v-if="hasMore || offset > 0" class="pagination">
        <button :disabled="loading || offset === 0" @click="prevPage">Previous</button>
        <span>{{ offset + 1 }}–{{ offset + issues.length }} of {{ total }}</span>
        <button :disabled="loading || !hasMore" @click="nextPage">Next</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import api from '@/services/api'
import auth from '@/services/auth'

const props = defineProps({
  projectId: { type: Number, required: true },
})

const emit = defineEmits(['updated'])

const loading = ref(false)
const error = ref(null)
const issues = ref([])
const total = ref(0)
const offset = ref(0)
const limit = ref(50)
const hasMore = ref(false)
const filterType = ref('')
const filterStatus = ref('')
const actionLoading = ref(null)

const canReview = computed(() => auth.hasPlatformAccess() || auth.hasCrossOrgComplianceAccess())

function entityLink(subjectId) {
  return `/projects/${props.projectId}/entities/${subjectId}`
}

function issueTypeLabel(type) {
  const map = {
    upsert_error: 'Upsert Error',
    duplicate: 'Duplicate',
    gap_fc_not_db: 'FC Gap',
    gap_db_not_fc: 'Stale in DB',
  }
  return map[type] || type
}

async function loadIssues() {
  loading.value = true
  error.value = null
  try {
    const params = new URLSearchParams({
      project_id: String(props.projectId),
      limit: String(limit.value),
      offset: String(offset.value),
    })
    if (filterType.value) params.append('issue_type', filterType.value)
    if (filterStatus.value) params.append('status', filterStatus.value)

    const response = await api.get(`/franconnect/sync-issues/?${params}`)
    const data = response.data?.data || {}
    issues.value = data.issues || []
    total.value = data.total || 0
    hasMore.value = Boolean(data.has_more)
  } catch (err) {
    error.value = err.response?.data?.error || 'Failed to load issues'
  } finally {
    loading.value = false
  }
}

async function reviewIssue(issue, status) {
  actionLoading.value = issue.issue_key
  try {
    await api.patch('/franconnect/sync-issues/', {
      project_id: props.projectId,
      issue_key: issue.issue_key,
      status,
    })
    await loadIssues()
    emit('updated')
  } catch (err) {
    error.value = err.response?.data?.error || 'Review action failed'
  } finally {
    actionLoading.value = null
  }
}

function resetAndLoad() {
  offset.value = 0
  loadIssues()
}

function prevPage() {
  offset.value = Math.max(0, offset.value - limit.value)
  loadIssues()
}

function nextPage() {
  offset.value += limit.value
  loadIssues()
}

function formatDateTime(dateString) {
  if (!dateString) return ''
  return new Date(dateString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

watch(() => props.projectId, resetAndLoad)

onMounted(loadIssues)

defineExpose({ refresh: loadIssues })
</script>

<style scoped>
.issues-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.issues-toolbar select {
  padding: 0.5rem 0.75rem;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
}

.btn-refresh {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 8px;
  background: #1e40af;
  color: white;
  cursor: pointer;
}

.issues-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.issue-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-left: 4px solid #94a3b8;
  border-radius: 8px;
  padding: 1rem 1.25rem;
}

.severity-error { border-left-color: #dc2626; }
.severity-duplicate { border-left-color: #d97706; }
.severity-gap { border-left-color: #2563eb; }
.severity-stale { border-left-color: #64748b; }

.issue-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.issue-type-badge {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  color: #64748b;
}

.issue-status {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  background: #f1f5f9;
}

.status-open { background: #fee2e2; color: #991b1b; }
.status-acknowledged { background: #fef3c7; color: #92400e; }
.status-dismissed { background: #f1f5f9; color: #475569; }
.status-resolved { background: #dcfce7; color: #166534; }

.issue-card h4 {
  margin: 0 0 0.35rem;
  font-size: 1rem;
}

.issue-desc {
  margin: 0 0 0.75rem;
  color: #64748b;
  font-size: 0.9rem;
}

.issue-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
}

.btn-link {
  color: #1e40af;
  font-weight: 600;
  text-decoration: none;
  font-size: 0.875rem;
}

.btn-action {
  padding: 0.35rem 0.75rem;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: white;
  font-size: 0.8rem;
  cursor: pointer;
}

.btn-ack { border-color: #fbbf24; color: #92400e; }
.btn-dismiss { border-color: #94a3b8; }
.btn-resolve { border-color: #86efac; color: #166534; }

.review-audit {
  margin-top: 0.75rem;
  font-size: 0.75rem;
  color: #94a3b8;
}

.pagination {
  display: flex;
  justify-content: center;
  gap: 1rem;
  align-items: center;
  margin-top: 1rem;
}

.loading-row,
.error-row,
.empty-row {
  text-align: center;
  padding: 2rem;
  color: #64748b;
}
</style>
